import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Auth state. Phase 2 T2.1 carries the bare-minimum surface so T2.2
 * (signup/login/MFA/password reset) can plug in without restructuring.
 *
 * The token is the Sanctum API key returned from /v1/auth/login. We
 * persist it under a single key so the page-reload flow keeps the user
 * signed in. localStorage is fine for MVP — Phase 2 may rotate this to
 * httpOnly cookies once the back-channel auth landing is built.
 */
export interface AuthUser {
  id: string;
  tenant_id: string;
  email: string;
  name: string | null;
  /** "admin" | "editor" | "viewer" — set on login per CP-01 §G. */
  role?: string;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  setSession: (input: { token: string; user: AuthUser }) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      setSession: ({ token, user }) => set({ token, user }),
      clear: () => set({ token: null, user: null }),
    }),
    {
      name: "email-builder.auth",
      partialize: (state) => ({ token: state.token, user: state.user }),
    },
  ),
);

export const getAuthToken = (): string | null => useAuthStore.getState().token;
